import { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import Hero from "../components/Hero";
import ProductCard from "../components/ProductCard";
import axios from "axios";
import Toast from "../components/Toast";
import Carousel from "../components/Carousel";

const FEATURES = [
  {
    num: "01",
    title: "CNC-Machined Aluminium Chassis",
    desc: "Milled from aerospace-grade billet aluminium. Zero plastic, zero flex — a body built for a lifetime of daily professional use.",
    tag: "Material Science",
  },
  {
    num: "02",
    title: "Tunable LED — 2700K to 6500K",
    desc: "Shift seamlessly from warm amber to crisp daylight. Flicker-free at every dimming level. Your circadian rhythm will notice.",
    tag: "LED Technology",
  },
  {
    num: "03",
    title: "Magnetic Quick-Release Head",
    desc: "The lamp head detaches magnetically in one motion. Tool-free removal, tool-free reattachment — aligned perfectly every time by the magnetic guides.",
    tag: "Magnetic Design",
  },
  {
    num: "04",
    title: "Touch Control — 3 Modes",
    desc: "A single touch cycles through three precision lighting modes. On/off with tap. No knobs, no switches. The surface is the interface.",
    tag: "Touch Technology",
  },
  {
    num: "05",
    title: "2-Year Comprehensive Warranty",
    desc: "Components rated to 50,000 hours. If anything fails, we replace it. No questions. No fine print.",
    tag: "Quality Assurance",
  },
];

const SPECS = [
  { val: "50",   unit: "K",   label: "Hours LED Lifespan" },
  { val: "2",    unit: "yr",  label: "Full Warranty" },
  { val: "360",  unit: "°",   label: "Articulation Range" },
  { val: "6500", unit: "K",   label: "Max Colour Temp" },
];

const QUICK_FEATURES = [
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
      </svg>
    ),
    title: "Studio-Grade Quality",
    desc: "Every component is selected against aerospace tolerance specs. Nothing ships unless it passes 14-point QA.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <circle cx="12" cy="12" r="3"/>
        <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12"/>
      </svg>
    ),
    title: "Tunable LED Spectrum",
    desc: "2700K warm to 6500K daylight. Full dimming range without flicker — tested at every frequency visible to the human eye.",
  },
  {
    icon: (
      <svg width="28" height="28" viewBox="0 0 24 24" fill="none"
        stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/>
      </svg>
    ),
    title: "2-Year Warranty",
    desc: "Comprehensive coverage, zero questions. Components rated to 50,000 hours. Built to outlast your workstation.",
  },
];


function SkeletonGrid() {
  return (
    <div className="hm-grid">
      {[...Array(6)].map((_, i) => (
        <div key={i} className="hm-skel-card">
          <div className="hm-skel" style={{ height: 260 }} />
          <div className="hm-skel-card-body">
            <div className="hm-skel" style={{ height: 16, width: "60%" }} />
            <div className="hm-skel" style={{ height: 13, width: "85%" }} />
            <div className="hm-skel" style={{ height: 13, width: "52%" }} />
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginTop: 8 }}>
              <div className="hm-skel" style={{ height: 22, width: "26%" }} />
              <div className="hm-skel" style={{ height: 32, width: "44%", borderRadius: 4 }} />
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function LampSVG2() {
  return (
    <svg viewBox="0 0 200 300" fill="none" xmlns="http://www.w3.org/2000/svg"
      style={{ width: "100%", maxWidth: "200px", height: "auto", display: "block" }}>
      <defs>
        <radialGradient id="l2-cone" cx="50%" cy="10%" r="90%">
          <stop offset="0%" stopColor="#10b981" stopOpacity="0.28"/>
          <stop offset="100%" stopColor="#10b981" stopOpacity="0"/>
        </radialGradient>
        <filter id="l2-glow" x="-80%" y="-80%" width="260%" height="260%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="6" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
        <filter id="l2-sglow" x="-50%" y="-50%" width="200%" height="200%">
          <feGaussianBlur in="SourceGraphic" stdDeviation="3" result="b"/>
          <feMerge><feMergeNode in="b"/><feMergeNode in="SourceGraphic"/></feMerge>
        </filter>
      </defs>

      {/* Floor glow */}
      <ellipse cx="100" cy="290" rx="80" ry="12" fill="#10b981" opacity="0.06" filter="url(#l2-glow)"/>

      {/* BASE */}
      <ellipse cx="100" cy="282" rx="52" ry="8" fill="rgba(0,0,0,0.22)"/>
      <ellipse cx="100" cy="276" rx="52" ry="10" fill="#0f172a"/>
      <ellipse cx="100" cy="272" rx="50" ry="9" fill="#1a2537"/>
      <ellipse cx="93" cy="269" rx="16" ry="3" fill="#2d4059" opacity="0.3"/>

      {/* ROD */}
      <rect x="95" y="108" width="10" height="166" rx="2.5" fill="#0f172a"/>
      <rect x="96" y="108" width="4" height="166" rx="1.5" fill="#2d4059" opacity="0.4"/>

      {/* MAGNETIC CONNECTOR */}
      <circle cx="100" cy="108" r="14" fill="#10b981" opacity="0.08" filter="url(#l2-glow)"/>
      <circle cx="100" cy="108" r="11" fill="#0f172a"/>
      <circle cx="100" cy="108" r="9" fill="#1a2537"/>
      <circle cx="100" cy="108" r="6" fill="#253347"/>
      <circle cx="100" cy="108" r="3.5" fill="#10b981" opacity="0.7" filter="url(#l2-sglow)"/>
      <circle cx="100" cy="108" r="1.5" fill="#d1fae5" opacity="0.9"/>

      {/* ARM from connector to head */}
      <line x1="100" y1="104" x2="72" y2="70" stroke="#0f172a" strokeWidth="9" strokeLinecap="round"/>
      <line x1="100" y1="104" x2="72" y2="70" stroke="#1a2537" strokeWidth="7" strokeLinecap="round"/>
      <line x1="99" y1="103" x2="73" y2="71" stroke="#2d4059" strokeWidth="2" strokeLinecap="round" opacity="0.45"/>

      {/* TOUCH SENSOR */}
      <circle cx="72" cy="62" r="11" fill="#0f172a"/>
      <circle cx="72" cy="62" r="9" fill="#1a2537"/>
      <circle cx="72" cy="62" r="5.5" fill="#253347"/>
      <circle cx="72" cy="62" r="3" fill="#10b981" opacity="0.7" filter="url(#l2-sglow)"/>
      <circle cx="72" cy="62" r="1.2" fill="#d1fae5" opacity="0.9"/>

      {/* SHADE */}
      <g transform="translate(72, 70) rotate(26)">
        <path d="M -12,-5 L 12,-5 L 30,44 L -30,44 Z" fill="#1a2537"/>
        <path d="M -9,-3 L 9,-3 L 27,42 L -27,42 Z" fill="#0c1520"/>
        <rect x="-13" y="-10" width="26" height="11" rx="2.5" fill="#1a2537"/>
        <rect x="-11" y="-8" width="22" height="8" rx="1.5" fill="#253347"/>
        <ellipse cx="0" cy="43" rx="30" ry="7" fill="#162030"/>
        <ellipse cx="0" cy="42" rx="28" ry="6" fill="#0c1520"/>
        {/* LED glow */}
        <ellipse cx="0" cy="35" rx="22" ry="6" fill="rgba(16,185,129,0.38)" filter="url(#l2-sglow)"/>
        <ellipse cx="0" cy="35" rx="14" ry="4" fill="rgba(16,185,129,0.68)" filter="url(#l2-sglow)"/>
        <ellipse cx="0" cy="35" rx="7" ry="2.5" fill="rgba(209,250,229,0.94)" filter="url(#l2-glow)"/>
        <ellipse cx="0" cy="35" rx="3" ry="1" fill="white" opacity="0.94"/>
      </g>

      {/* Light cone */}
      <path d="M 44 76 L -14 220 L 158 220 L 112 76 Z" fill="url(#l2-cone)" opacity="0.4"/>
      <ellipse cx="72" cy="220" rx="86" ry="12" fill="#10b981" opacity="0.05"/>

      {/* LED bloom */}
      <ellipse cx="64" cy="100" rx="22" ry="16" fill="#10b981" opacity="0.1" filter="url(#l2-glow)"/>
    </svg>
  );
}

function Home({ cart, setCart }) {
  const navigate = useNavigate();
  const location = useLocation();
  const [products, setProducts] = useState([]);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const gridRef = useRef(null);
  const featRef = useRef(null);

  /* Scroll to a section when arriving from another page via navbar */
  useEffect(() => {
    const target = location.state?.scrollTo;
    if (!target) return;
    const el = document.getElementById(target);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  }, [location.state]);

  useEffect(() => {
    axios
      .get(`${process.env.REACT_APP_API_BASE_URL}/products`)
      .then((res) => { setProducts(res.data); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  /* Scroll-reveal for feature items */
  useEffect(() => {
    const items = featRef.current?.querySelectorAll(".hm-feat2");
    if (!items?.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) e.target.classList.add("vis"); }),
      { threshold: 0.15 }
    );
    items.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, []);

  /* Scroll-reveal for product cards */
  useEffect(() => {
    if (!products.length) return;
    const io = new IntersectionObserver(
      (entries) => entries.forEach((e) => {
        if (e.isIntersecting) e.target.classList.add("vis");
      }),
      { threshold: 0.07 }
    );
    const cards = gridRef.current?.querySelectorAll(".hm-card-reveal");
    cards?.forEach((c) => io.observe(c));
    return () => io.disconnect();
  }, [products]);

  const addToCart = (product) => {
    const existing = cart.find((p) => p.id === product.id);
    if (existing) {
      setCart(cart.map((p) =>
        p.id === product.id ? { ...p, quantity: p.quantity + 1 } : p
      ));
    } else {
      setCart([...cart, { ...product, quantity: 1 }]);
    }
    setToastMessage("Added to cart");
    setShowToast(true);
  };

  const scrollTo = (id) =>
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });

  return (
    <div>
      <Toast
        show={showToast}
        setShow={setShowToast}
        title="Added to Cart"
        message={toastMessage}
        variant="success"
        actions={[
          { label: "View Cart", variant: "primary", onClick: () => { setShowToast(false); navigate("/cart"); } },
        ]}
      />

      {/* ── Hero ── */}
      <Hero />

      {/* ── Brand marquee ── */}
      <Carousel />

      {/* ── Products ── */}
      <section id="collection" className="hm-products-wrap">
        <div className="hm-section">
          <div className="hm-products-head">
            <div>
              <div className="hm-eyebrow">The Collection</div>
              <h2 className="hm-title">Every Product</h2>
              <div className="hm-divider" />
            </div>
            {!loading && products.length > 0 && (
              <span className="hm-products-count">{products.length} items</span>
            )}
          </div>

          {loading && <SkeletonGrid />}

          {!loading && products.length === 0 && (
            <div className="hm-empty">
              <div className="hm-empty-icon">
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="1.4" strokeLinecap="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 2v3M12 19v3M4.22 4.22l2.12 2.12M17.66 17.66l2.12 2.12M2 12h3M19 12h3M4.22 19.78l2.12-2.12M17.66 6.34l2.12-2.12" />
                </svg>
              </div>
              <span className="hm-empty-txt">No products yet</span>
            </div>
          )}

          {!loading && products.length > 0 && (
            <div ref={gridRef} className="hm-grid">
              {products.map((p, i) => (
                <div
                  key={p.id}
                  className="hm-card-reveal"
                  style={{ transitionDelay: `${(i % 3) * 0.08}s` }}
                >
                  <ProductCard product={p} addToCart={addToCart} />
                </div>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* ── Quick features strip ── */}
      <div className="hm-qf-wrap">
        <div className="hm-qf-grid">
          {QUICK_FEATURES.map((f) => (
            <div key={f.title} className="hm-qf-item">
              <div className="hm-qf-icon">{f.icon}</div>
              <div className="hm-qf-title">{f.title}</div>
              <p className="hm-qf-desc">{f.desc}</p>
            </div>
          ))}
        </div>
      </div>

      {/* ── Statement ── */}
      <section className="hm-statement">
        <div className="hm-statement-glow" />
        <div className="hm-statement-inner">
          <p className="hm-statement-tag">
            <span />The TableCure Standard<span />
          </p>
          <h2 className="hm-statement-hl">
            Light is not <em>decoration.</em>
            <br />It is infrastructure.
          </h2>
          <p className="hm-statement-body">
            Every product in the TableCure collection is built for people who
            treat their workspace the way engineers treat their instruments —
            with precision, intention, and zero tolerance for compromise.
          </p>
          <button className="hm-statement-cta" onClick={() => scrollTo("collection")}>
            Explore the Collection
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="1.6" strokeLinecap="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </section>

      {/* ── Spec strip ── */}
      <div className="hm-spec-strip">
        <div className="hm-spec-inner">
          {SPECS.map((s) => (
            <div key={s.label} className="hm-spec-item">
              <div>
                <span className="hm-spec-val">{s.val}</span>
                <span className="hm-spec-unit">{s.unit}</span>
              </div>
              <div className="hm-spec-label">{s.label}</div>
            </div>
          ))}
        </div>
      </div>

      {/* ── Why TableCure section ── */}
      <section id="about" className="hm-spotlight-wrap">
        <div className="hm-spotlight">

          {/* Left — lamp visualization */}
          <div className="hm-spotlight-vis">
            <div className="hm-spotlight-lamp">
              <LampSVG2 />
              <div className="hm-spotlight-lamp-glow" />
            </div>
          </div>

          {/* Right — scroll-reveal feature list */}
          <div className="hm-spotlight-content">
            <div>
              <div className="hm-eyebrow">Why TableCure</div>
              <h2 className="hm-title">Built Different</h2>
              <div className="hm-divider" />
            </div>

            <div className="hm-feat-list" ref={featRef}>
              {FEATURES.map((f, i) => (
                <div key={f.num} className="hm-feat2" style={{ transitionDelay: `${i * 0.05}s` }}>
                  <div className="hm-feat2-track">
                    <div className="hm-feat2-dot" />
                    <div className="hm-feat2-line" />
                  </div>
                  <div className="hm-feat2-body">
                    <div className="hm-feat2-num">{f.num}</div>
                    <h3 className="hm-feat2-title">{f.title}</h3>
                    <p className="hm-feat2-desc">{f.desc}</p>
                    <span className="hm-feat-tag">{f.tag}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── CTA Banner ── */}
      <div className="hm-cta-banner">
        <div className="hm-cta-bg" />
        <div className="hm-cta-inner">
          <h2 className="hm-cta-title">Your Desk.<br />Elevated.</h2>
          <p className="hm-cta-sub">
            Free worldwide shipping on every order. Join thousands of engineers,
            designers, and researchers who upgraded their workspace with TableCure.
          </p>
          <button className="hm-cta-btn" onClick={() => scrollTo("collection")}>
            Shop the Collection
            <svg width="13" height="13" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2" strokeLinecap="round">
              <path d="M5 12h14M12 5l7 7-7 7" />
            </svg>
          </button>
        </div>
      </div>
    </div>
  );
}

export default Home;
